<template>
  <div class="embeddings-playground bg-gray-50 dark:bg-gray-900 p-6">
    <div class="max-w-3xl mx-auto">
      <Card>
        <h3
          class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
            />
          </svg>
          Text Embeddings
        </h3>

        <!-- No Models Warning -->
        <Alert v-if="!isLoadingModels && models.length === 0" variant="warning" class="mb-4">
          No embeddings models available. Please check your API configuration.
        </Alert>

        <!-- Model Picker -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >Model</label
          >
          <Select
            :model-value="selectedModel"
            :options="modelOptions"
            :disabled="isLoadingModels || models.length === 0"
            placeholder="Select embeddings model"
            @update:model-value="handleModelChange"
          />
        </div>

        <!-- Text Input -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >Input Text</label
          >
          <textarea
            v-model="inputText"
            rows="4"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter text to generate embeddings..."
          />
        </div>

        <!-- Task type (Gemini embeddings only — driven by metadata.task_types) -->
        <div v-if="taskTypeOptions.length > 0" class="mb-4">
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Task type <span class="text-gray-400">(optional)</span>
          </label>
          <Select
            :model-value="taskType"
            :options="taskTypeOptions"
            placeholder="auto"
            @update:model-value="(v: string) => (taskType = v)"
          />
          <p class="text-xs text-gray-500 mt-1">
            Hints the model about the downstream use of the embedding (retrieval, classification, etc.).
          </p>
        </div>

        <!-- Request Options -->
        <div v-if="dimensionsMax > 0" class="mb-4">
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Dimensions <span class="text-gray-400">(optional, {{ dimensionsMin }}–{{ dimensionsMax }})</span>
          </label>
          <input
            :value="dimensions ?? ''"
            type="number"
            :min="dimensionsMin"
            :max="dimensionsMax"
            :placeholder="`model default (${dimensionsMax})`"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
            @input="(e: Event) => {
              const v = (e.target as HTMLInputElement).value;
              dimensions = v ? Number(v) : null;
            }"
          />
        </div>

        <!-- Generate Button -->
        <Button
          variant="primary"
          :disabled="!canGenerate || isLoading"
          :loading="isLoading"
          @click="handleGenerate"
          class="w-full"
        >
          Generate Embeddings
        </Button>

        <!-- Results -->
        <div v-if="embeddings" class="mt-6">
          <div class="flex items-center justify-between mb-2">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Embeddings
            </label>
            <div class="flex gap-2">
              <Button variant="ghost" size="sm" @click="copyEmbeddings">
                <span v-if="copied">Copied</span>
                <span v-else>Copy</span>
              </Button>
              <Button variant="ghost" size="sm" @click="downloadEmbeddings">Download</Button>
            </div>
          </div>

          <!-- Size summary -->
          <div class="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-400">
            <span><strong>Dimensions:</strong> {{ embeddings.length }}</span>
            <span><strong>Payload:</strong> {{ formatBytes(responseBytes) }}</span>
            <span>
              <strong>Range:</strong>
              [{{ embeddingsMin.toFixed(4) }}, {{ embeddingsMax.toFixed(4) }}]
            </span>
          </div>

          <div class="p-4 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-auto max-h-64">
            <code class="text-xs text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-all">
              [{{ embeddings.slice(0, 20).join(', ') }}{{ embeddings.length > 20 ? ', ...' : '' }}]
            </code>
          </div>
          <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Showing first 20 of {{ embeddings.length }} dimensions
          </p>
        </div>
      </Card>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue';
  import { Alert, Button, Select, Card } from '@asabdev/asab-ui';
  import { useEmbeddingsPlayground } from '../../composables/useEmbeddingsPlayground';
  import type { Model } from '../../types/models';

  interface Props {
    models: Model[];
    isLoadingModels: boolean;
  }

  const props = defineProps<Props>();

  const {
    inputText,
    selectedModel,
    dimensions,
    taskType,
    embeddings,
    isLoading,
    generateEmbeddings,
    setModel,
  } = useEmbeddingsPlayground();

  const modelOptions = computed(() => props.models.map(m => ({ value: m.id, label: m.name })));

  const canGenerate = computed(() => inputText.value.trim() && selectedModel.value);

  // Metadata-driven dimension bounds (0 = model doesn't support dim reduction).
  const currentModel = computed(() =>
    props.models.find(m => m.id === selectedModel.value)
  );
  const metaNum = (key: string, fallback: number): number => {
    const v = currentModel.value?.metadata?.[key];
    return typeof v === 'number' ? v : fallback;
  };
  const dimensionsMin = computed(() => metaNum('dimensions_min', 1));
  const dimensionsMax = computed(() => metaNum('dimensions_max', 0));

  const taskTypeOptions = computed(() => {
    const arr = currentModel.value?.metadata?.task_types;
    if (!Array.isArray(arr)) return [];
    return (arr as string[]).map(t => ({ value: t, label: t }));
  });

  onMounted(() => {
    if (props.models.length > 0 && !selectedModel.value) {
      setModel(props.models[0].id);
    }
  });

  const handleModelChange = (value: string) => setModel(value);

  const handleGenerate = async () => {
    await generateEmbeddings(inputText.value);
  };

  const copied = ref(false);
  const copyEmbeddings = async () => {
    if (!embeddings.value) return;
    await navigator.clipboard.writeText(JSON.stringify(embeddings.value));
    copied.value = true;
    setTimeout(() => (copied.value = false), 1500);
  };

  const downloadEmbeddings = () => {
    if (!embeddings.value) return;
    const payload = {
      model: selectedModel.value,
      input: inputText.value,
      dimensions: embeddings.value.length,
      embedding: embeddings.value,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const slug = (selectedModel.value || 'embedding').replace(/[^a-zA-Z0-9_-]/g, '_');
    a.download = `${slug}-${embeddings.value.length}d.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Approximate wire-size: JSON.stringify gives the same size the API emitted.
  const responseBytes = computed(() =>
    embeddings.value ? new Blob([JSON.stringify(embeddings.value)]).size : 0
  );
  const embeddingsMin = computed(() =>
    embeddings.value && embeddings.value.length ? Math.min(...embeddings.value) : 0
  );
  const embeddingsMax = computed(() =>
    embeddings.value && embeddings.value.length ? Math.max(...embeddings.value) : 0
  );

  const formatBytes = (n: number): string => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  };
</script>
